import { useEditPanelViewport } from '../../ui/use-edit-panel-viewport.js';
import { ClientAvatar as AvatarSchema, type ClientAvatar as AvatarRecord } from '@diana/contracts';
import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useRef, useState } from 'react';
import { Check, Search, Trash2, Upload, X } from 'lucide-react';
import type { AdminApiClient } from '../../lib/api-client.js';
import { useDianaUIPortal } from '../../ui/index.js';
import { announceAvatarChange, ClientAvatar } from './ClientAvatar.js';

export function ClientAvatarEditor({
  id,
  name,
  api,
}: {
  id: string;
  name: string;
  api: AdminApiClient;
}) {
  const portal = useDianaUIPortal();
  const [open, setOpen] = useState(false);
  const { panelRef, bodyRef } = useEditPanelViewport(open);
  const [metadata, setMetadata] = useState<AvatarRecord | null>(null);
  const [busy, setBusy] = useState(false);
  const gate = useRef(false);
  const [error, setError] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const discardFile = () => {
    setFile(null);
    if (fileInput.current) fileInput.current.value = '';
  };
  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const base = `/api/v1/admin/clients/${id}/avatar`;
  const load = async () => {
    const result = await api.get(base, AvatarSchema);
    setMetadata((previous) => (previous && previous.version > result.version ? previous : result));
    return result;
  };
  useEffect(() => {
    if (!open) return;
    let active = true;
    let seenVersion = -1;
    const read = () =>
      api
        .get(base, AvatarSchema)
        .then((result) => {
          if (active) {
            setMetadata((previous) =>
              previous && previous.version > result.version ? previous : result,
            );
            if (seenVersion !== result.version) {
              announceAvatarChange(id);
              seenVersion = result.version;
            }
          }
        })
        .catch(() => {
          if (active) setError('Could not load photo settings. Close and try again.');
        });
    void read();
    const interval = setInterval(() => void read(), 5_000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [open, api, base, id]);
  async function mutate(action: 'upload' | 'refresh' | 'remove') {
    if (gate.current || !metadata) return;
    gate.current = true;
    setBusy(true);
    setError('');
    try {
      let image: string | undefined;
      if (action === 'upload') {
        if (
          !file ||
          file.size > 512_000 ||
          !['image/jpeg', 'image/png', 'image/webp'].includes(file.type)
        )
          throw new Error('image');
        const bytes = new Uint8Array(await file.arrayBuffer());
        let binary = '';
        for (const byte of bytes) binary += String.fromCharCode(byte);
        image = btoa(binary);
      }
      const result = await api.request(`${base}/${action}`, {
        method: 'POST',
        body: { expectedVersion: metadata.version, ...(image ? { image } : {}) },
        schema: AvatarSchema,
      });
      setMetadata((previous) =>
        previous && previous.version > result.version ? previous : result,
      );
      setFile(null);
      announceAvatarChange(id);
    } catch {
      setError(
        action === 'upload'
          ? 'Could not save photo. Choose a JPEG, PNG or WebP under 500 KB and retry.'
          : 'Could not update photo. It may have changed or been checked recently. Try again later.',
      );
      await load().catch(() => undefined);
    } finally {
      gate.current = false;
      setBusy(false);
    }
  }
  const recentlyChecked = Boolean(
    metadata?.checkedAt && Date.now() - Date.parse(metadata.checkedAt) < 15 * 60_000,
  );
  const status =
    metadata?.status === 'Pending'
      ? 'Looking for a photo…'
      : metadata?.status === 'Disabled'
        ? 'Automatic lookup is off.'
        : metadata?.source
          ? `Photo from ${metadata.source}`
          : metadata?.status === 'Unavailable'
            ? 'Lookup unavailable. You can upload a photo.'
            : 'No photo found. You can upload one.';
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(value) => {
        if (!gate.current) {
          if (!value && file && !window.confirm('Discard selected photo?')) return;
          setOpen(value);
          if (value) {
            setError('');
            setMetadata(null);
            setFile(null);
          }
        }
      }}
    >
      <Dialog.Trigger className="client-avatar-editor__trigger" aria-label="Change client photo">
        <ClientAvatar id={id} name={name} className="client-profile__initials" />
      </Dialog.Trigger>
      <Dialog.Portal container={portal}>
        <Dialog.Overlay className="shoot-destructive__overlay" />
        <Dialog.Content
          ref={panelRef}
          className="shoot-destructive client-avatar-editor dui-compact-panel"
          data-compact-panel
          onEscapeKeyDown={(event) => {
            if (busy) event.preventDefault();
          }}
          onPointerDownOutside={(event) => {
            if (busy) event.preventDefault();
          }}
        >
          <header className="client-avatar-editor__header" data-panel-header>
            <Dialog.Title>Client photo</Dialog.Title>
            <Dialog.Close
              className="shoot-button shoot-button--outline shoot-button--icon"
              aria-label="Close"
              title="Close client photo"
              disabled={busy}
            >
              <X size={20} aria-hidden="true" />
            </Dialog.Close>
          </header>
          <Dialog.Description className="sr-only">
            Upload a client photo or find one from Instagram and email. Uploads are saved only when
            you press Save photo.
          </Dialog.Description>
          <div
            ref={bodyRef}
            className="client-avatar-editor__fields"
            aria-busy={busy}
            data-panel-body
          >
            <div className="client-avatar-editor__summary">
              <div className="client-avatar-editor__preview">
                {preview ? (
                  <img src={preview} alt="Selected client photo" />
                ) : (
                  <ClientAvatar id={id} name={name} />
                )}
              </div>
              <div className="client-avatar-editor__identity">
                <strong>{name}</strong>
                <p className="client-avatar-editor__status" role="status">
                  {file ? 'Selected photo, not saved' : metadata ? status : 'Loading…'}
                </p>
              </div>
              {file || metadata?.source ? (
                <button
                  type="button"
                  className="shoot-button shoot-button--text shoot-button--icon client-avatar-editor__remove"
                  aria-label={file ? 'Discard selected photo' : 'Remove photo'}
                  title={file ? 'Discard selected photo' : 'Remove photo'}
                  disabled={busy || !metadata}
                  onClick={file ? discardFile : () => void mutate('remove')}
                >
                  {file ? (
                    <X size={19} aria-hidden="true" />
                  ) : (
                    <Trash2 size={19} aria-hidden="true" />
                  )}
                </button>
              ) : null}
            </div>
            <div className="client-avatar-editor__actions">
              <label className="shoot-button shoot-button--outline client-avatar-editor__upload">
                <Upload size={18} aria-hidden="true" />
                {file ? 'Choose another' : 'Upload photo'}
                <input
                  ref={fileInput}
                  key={metadata?.version ?? -1}
                  aria-label="Upload photo"
                  className="client-avatar-editor__file"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  disabled={busy || !metadata}
                  onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                />
              </label>
              <button
                type="button"
                className="shoot-button shoot-button--outline"
                disabled={
                  busy ||
                  Boolean(file) ||
                  !metadata ||
                  metadata.source === 'Manual' ||
                  metadata.status === 'Pending' ||
                  recentlyChecked
                }
                title={recentlyChecked ? 'Recently checked. Try again in 15 minutes.' : undefined}
                onClick={() => void mutate('refresh')}
              >
                <Search size={18} aria-hidden="true" />
                Find photo
              </button>
            </div>
            <p className="client-avatar-editor__hint">
              {file
                ? file.name
                : metadata?.source === 'Manual'
                  ? 'Remove your uploaded photo to use automatic lookup.'
                  : recentlyChecked && metadata?.status !== 'Pending'
                    ? 'Checked recently. Try again in 15 minutes.'
                    : 'JPG, PNG or WebP · Up to 500 KB'}
            </p>
            {error ? (
              <p className="client-avatar-editor__error" role="alert">
                {error}
              </p>
            ) : null}
          </div>
          {file ? (
            <footer className="client-avatar-editor__save" data-panel-footer>
              <button
                type="button"
                className="shoot-button shoot-button--outline"
                disabled={busy}
                onClick={discardFile}
              >
                Cancel
              </button>
              <button
                type="button"
                className="shoot-button shoot-button--primary"
                disabled={busy || !metadata}
                onClick={() => void mutate('upload')}
              >
                <Check size={18} aria-hidden="true" />
                {busy ? 'Saving…' : 'Save photo'}
              </button>
            </footer>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
